/**
 * RecoveryService — pure-function service for healing and fatigue recovery.
 *
 * Provides damage state classification, natural healing rates, and outcome
 * calculations for First Aid, Extended Care, Meditation, Electrical Succour,
 * and Prosanguine. Follows the same stateless pattern as HandednessService
 * and CraftsmanshipService.
 */

import type { RollResult } from '../types/dice';
import { Difficulty, ModifierSource } from '../types/dice';
import type { Character } from '../types/character';
import type { DiceService } from './DiceService';
import { TalentsService } from './TalentsService';

// ── Enums & Types ────────────────────────────────────────────────────

export enum DamageState {
  LightlyDamaged = 'lightly-damaged',
  HeavilyDamaged = 'heavily-damaged',
  CriticallyDamaged = 'critically-damaged'
}

export interface NaturalHealingRate {
  amount: number;
  period: 'day' | 'week' | 'none';
  bedRestAmount: number;
  bedRestPeriod: 'day' | 'week' | 'none';
  label: string;
  bedRestLabel: string;
}

export interface FirstAidResult {
  rollResult: RollResult;
  damageHealed: number;
  damageState: DamageState;
}

export interface ExtendedCareResult {
  rollResult: RollResult;
  damageHealed: number;
  damageState: DamageState;
  dofSeverity?: 'minor' | 'major';
}

export interface MeditationResult {
  rollResult: RollResult;
  fatigueRemoved: number;
}

export interface ElectricalSuccourResult {
  rollResult: RollResult;
  fatigueRemoved: number;
}

export interface ProsanguineResult {
  rollResult: RollResult;
  damageHealed: number;
  overstrained: boolean;
}

// ── Pure Calculation Functions ────────────────────────────────────────

/**
 * Classify damage state from character data.
 *
 * Hardy and Autosanguine talents override to LightlyDamaged regardless
 * of actual damage (RT p.562, p.249). Otherwise:
 * - criticalDamageCount > 0 → CriticallyDamaged (RT p.262)
 * - totalDamage > 2 × toughnessBonus → HeavilyDamaged (RT p.262)
 * - Otherwise → LightlyDamaged (RT p.262)
 */
export function classifyDamageState(
  totalDamage: number,
  toughnessBonus: number,
  criticalDamageCount: number,
  hasHardy: boolean,
  hasAutosanguine: boolean
): DamageState {
  if (hasHardy || hasAutosanguine) {
    return DamageState.LightlyDamaged;
  }

  if (criticalDamageCount > 0) {
    return DamageState.CriticallyDamaged;
  }

  if (totalDamage > 2 * toughnessBonus) {
    return DamageState.HeavilyDamaged;
  }

  return DamageState.LightlyDamaged;
}

/**
 * Calculate natural healing rate for a given damage state.
 *
 * Autosanguine overrides the standard rate to 2 damage/day regardless
 * of damage state (RT p.252). Otherwise:
 * - LightlyDamaged: 1/day, bed rest TB/day (RT p.262)
 * - HeavilyDamaged: 1/week, bed rest TB/week (RT p.262)
 * - CriticallyDamaged: no natural healing, bed rest 1 critical/week
 *   with medical attention (RT p.262)
 */
export function getNaturalHealingRate(
  damageState: DamageState,
  toughnessBonus: number,
  hasAutosanguine: boolean
): NaturalHealingRate {
  if (hasAutosanguine) {
    return {
      amount: 2,
      period: 'day',
      bedRestAmount: toughnessBonus,
      bedRestPeriod: 'day',
      label: '2 damage per day (Autosanguine)',
      bedRestLabel: `${toughnessBonus} damage per day (bed rest)`
    };
  }

  switch (damageState) {
    case DamageState.LightlyDamaged:
      return {
        amount: 1,
        period: 'day',
        bedRestAmount: toughnessBonus,
        bedRestPeriod: 'day',
        label: '1 damage per day',
        bedRestLabel: `${toughnessBonus} damage per day (bed rest)`
      };
    case DamageState.HeavilyDamaged:
      return {
        amount: 1,
        period: 'week',
        bedRestAmount: toughnessBonus,
        bedRestPeriod: 'week',
        label: '1 damage per week',
        bedRestLabel: `${toughnessBonus} damage per week (bed rest)`
      };
    case DamageState.CriticallyDamaged:
      return {
        amount: 0,
        period: 'none',
        bedRestAmount: 1,
        bedRestPeriod: 'week',
        label: 'No natural healing — requires medical attention',
        bedRestLabel: '1 Critical Damage per week (medical attention + bed rest)'
      };
  }
}

/**
 * Calculate First Aid healing amount.
 *
 * On a successful Medicae test (RT p.83):
 * - LightlyDamaged: heals IB (Intelligence Bonus) damage
 * - HeavilyDamaged or CriticallyDamaged: heals 1 damage
 * On failure: no damage healed.
 */
export function calculateFirstAidHealing(
  damageState: DamageState,
  intelligenceBonus: number,
  success: boolean
): number {
  if (!success) {
    return 0;
  }

  if (damageState === DamageState.LightlyDamaged) {
    return intelligenceBonus;
  }

  return 1;
}

/**
 * Calculate Extended Care outcome.
 *
 * On a successful Medicae test (RT p.83):
 * - Heals 2× the natural healing amount + DoS
 *   (Lightly: 2×1=2, Heavily: 2×1=2, Critically: 2×0=0)
 * On failure (RT p.83):
 * - 0-1 DoF: no effect (patients heal at normal rate)
 * - 2-3 DoF: deals 1 damage (minor severity)
 * - 4+ DoF: signals 1d10 damage roll (major severity)
 */
export function calculateExtendedCareOutcome(
  damageState: DamageState,
  toughnessBonus: number,
  success: boolean,
  degreesOfSuccess: number,
  degreesOfFailure: number
): { damageChange: number; severity?: 'minor' | 'major' } {
  if (success) {
    // Natural healing amount per damage state (without Autosanguine override)
    const naturalHealingAmount = damageState === DamageState.CriticallyDamaged ? 0 : 1;
    const healing = 2 * naturalHealingAmount + degreesOfSuccess;
    return { damageChange: healing };
  }

  // Failure tiers
  if (degreesOfFailure >= 4) {
    return { damageChange: 0, severity: 'major' };
  }

  if (degreesOfFailure >= 2) {
    return { damageChange: -1, severity: 'minor' };
  }

  // 0-1 DoF: no effect
  return { damageChange: 0 };
}

/**
 * Calculate Meditation outcome.
 *
 * On a successful Willpower test (RT p.790):
 * - Removes 1 level of Fatigue
 * On failure: no fatigue removed.
 */
export function calculateMeditationOutcome(success: boolean): number {
  return success ? 1 : 0;
}

/**
 * Calculate Electrical Succour outcome.
 *
 * On a successful Ordinary (+10) Toughness test (RT p.427):
 * - Removes 1 level of Fatigue plus 1 per additional Degree of Success
 * On failure: no fatigue removed.
 */
export function calculateElectricalSuccourOutcome(
  success: boolean,
  degreesOfSuccess: number
): number {
  return success ? 1 + degreesOfSuccess : 0;
}

// ── Orchestration Functions ──────────────────────────────────────────

/**
 * Perform First Aid: roll Medicae skill test, compute healing, log to history.
 *
 * 1. Classify the character's damage state
 * 2. Roll a Medicae skill test via DiceService
 * 3. Calculate healing amount via calculateFirstAidHealing
 * 4. Log to roll history with title "Medicae First Aid"
 * 5. Return FirstAidResult
 */
export function performFirstAid(
  character: Character,
  diceService: DiceService,
  useTechUse = false
): FirstAidResult {
  const woundsTracking = character.progression!.woundsTracking;
  const toughnessBonus = Math.floor(character.characteristics.toughness / 10);
  const intelligenceBonus = Math.floor(character.characteristics.intelligence / 10);
  const criticalDamageCount = woundsTracking.criticalDamage.length;

  const hasHardy = TalentsService.hasTalent(character.talents, 'hardy');
  const hasAutosanguine = TalentsService.hasTalent(character.talents, 'autosanguine');

  const damageState = classifyDamageState(
    woundsTracking.totalDamage,
    toughnessBonus,
    criticalDamageCount,
    hasHardy,
    hasAutosanguine
  );

  const skill = useTechUse ? 'tech-use' : 'medicae';
  const modifiers = useTechUse
    ? [{ name: 'Physical Perfection', value: -20, source: ModifierSource.Talent }]
    : [];
  const rollResult = diceService.rollSkill(character, skill, modifiers, undefined, {
    title: useTechUse ? 'Tech-Use First Aid' : 'Medicae First Aid',
    tags: ['recovery', 'healing', 'first-aid']
  });

  const damageHealed = calculateFirstAidHealing(damageState, intelligenceBonus, rollResult.success);

  return {
    rollResult,
    damageHealed,
    damageState
  };
}

/**
 * Perform Extended Care: roll Medicae skill test, compute outcome, log to history.
 *
 * 1. Classify the character's damage state
 * 2. Roll a Medicae skill test via DiceService
 * 3. Calculate outcome via calculateExtendedCareOutcome
 * 4. On major failure (4+ DoF), roll 1d10 for damage dealt
 * 5. Log to roll history with title "Medicae Extended Care"
 * 6. Return ExtendedCareResult
 */
export function performExtendedCare(
  character: Character,
  diceService: DiceService,
  useTechUse = false
): ExtendedCareResult {
  const woundsTracking = character.progression!.woundsTracking;
  const toughnessBonus = Math.floor(character.characteristics.toughness / 10);
  const criticalDamageCount = woundsTracking.criticalDamage.length;

  const hasHardy = TalentsService.hasTalent(character.talents, 'hardy');
  const hasAutosanguine = TalentsService.hasTalent(character.talents, 'autosanguine');

  const damageState = classifyDamageState(
    woundsTracking.totalDamage,
    toughnessBonus,
    criticalDamageCount,
    hasHardy,
    hasAutosanguine
  );

  const skill = useTechUse ? 'tech-use' : 'medicae';
  const modifiers = useTechUse
    ? [{ name: 'Physical Perfection', value: -20, source: ModifierSource.Talent }]
    : [];
  const rollResult = diceService.rollSkill(character, skill, modifiers, undefined, {
    title: useTechUse ? 'Tech-Use Extended Care' : 'Medicae Extended Care',
    tags: ['recovery', 'healing', 'extended-care']
  });

  const degreesOfSuccess = rollResult.success ? rollResult.degrees : 0;
  const degreesOfFailure = rollResult.success ? 0 : Math.abs(rollResult.degrees);

  const outcome = calculateExtendedCareOutcome(
    damageState,
    toughnessBonus,
    rollResult.success,
    degreesOfSuccess,
    degreesOfFailure
  );

  let damageHealed = outcome.damageChange;

  // On major failure, roll 1d10 for damage dealt
  if (outcome.severity === 'major') {
    const d10Roll = diceService.rollDie(10);
    damageHealed = -d10Roll;
  }

  return {
    rollResult,
    damageHealed,
    damageState,
    dofSeverity: outcome.severity
  };
}

/**
 * Perform Meditation: roll Willpower characteristic test, compute fatigue removal, log to history.
 *
 * 1. Roll a Willpower characteristic test at Ordinary (+10) difficulty via DiceService
 * 2. Calculate fatigue removal via calculateMeditationOutcome
 * 3. Log to roll history with title "Meditation (Fatigue Removal)"
 * 4. Return MeditationResult
 */
export function performMeditation(
  character: Character,
  diceService: DiceService
): MeditationResult {
  const rollResult = diceService.rollCharacteristic(
    character,
    'willpower',
    [],
    Difficulty.Ordinary,
    {
      title: 'Meditation (Fatigue Removal)',
      tags: ['recovery', 'fatigue', 'meditation']
    }
  );

  const fatigueRemoved = calculateMeditationOutcome(rollResult.success);

  return {
    rollResult,
    fatigueRemoved
  };
}

/**
 * Perform Electrical Succour: roll Toughness characteristic test, compute fatigue removal, log to history.
 *
 * 1. Roll a Toughness characteristic test at Ordinary (+10) difficulty via DiceService
 * 2. Calculate fatigue removal via calculateElectricalSuccourOutcome
 * 3. Log to roll history with title "Electrical Succour (Fatigue Removal)"
 * 4. Return ElectricalSuccourResult
 */
export function performElectricalSuccour(
  character: Character,
  diceService: DiceService
): ElectricalSuccourResult {
  const rollResult = diceService.rollCharacteristic(
    character,
    'toughness',
    [],
    Difficulty.Ordinary,
    {
      title: 'Electrical Succour (Fatigue Removal)',
      tags: ['recovery', 'fatigue', 'electrical-succour']
    }
  );

  const degreesOfSuccess = rollResult.success ? rollResult.degrees : 0;
  const fatigueRemoved = calculateElectricalSuccourOutcome(rollResult.success, degreesOfSuccess);

  return {
    rollResult,
    fatigueRemoved
  };
}

/**
 * Perform Prosanguine: roll Tech-Use skill test, compute healing, log to history.
 *
 * 1. Roll a Tech-Use skill test via DiceService
 * 2. Check for overstrain: if the raw d100 roll is 96-100, set overstrained with no healing
 * 3. On success (without overstrain), roll 1d5 via DiceService.rollDie(5) for healing amount
 * 4. Log to roll history with title "Prosanguine Healing"
 * 5. Return ProsanguineResult
 */
export function performProsanguine(
  character: Character,
  diceService: DiceService
): ProsanguineResult {
  const rollResult = diceService.rollSkill(character, 'tech-use', [], undefined, {
    title: 'Prosanguine Healing',
    tags: ['recovery', 'healing', 'prosanguine']
  });

  // Overstrain on raw d100 roll of 96-100: no healing applied
  if (rollResult.raw >= 96) {
    return {
      rollResult,
      damageHealed: 0,
      overstrained: true
    };
  }

  // On success, roll 1d5 for healing amount
  const damageHealed = rollResult.success ? diceService.rollDie(5) : 0;

  return {
    rollResult,
    damageHealed,
    overstrained: false
  };
}
